import { User, UserSettings } from '@paragon/contract-library';
import { AspNetStatusCodeResult, designService } from '@paragon/data-connections';
import { PathUtilities } from '@paragon/layout-models';
import {
  automaticLogInAttemptComplete,
  automaticLogInAttemptStarted,
  getLoggedInUser,
  hasAssemblyPermission,
  hasFreeSubscription,
  isG2User,
  isWithinFreeTrialPeriod,
  logInWithGuids,
  logInWithStoredToken,
  logInWithTransientToken,
} from '@paragon/login-tools';
import { loadCurrentOrganizationMaterialSet } from '@paragon/material-lists';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { getFallbackUserSettings } from '@paragon/user-settings';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import g2FaviconUrl from '../../../resources/G2Favicon.ico';
import { isSelectableItemType, LEGACY_BEARING_ENVELOPE_URL_TYPE } from '../../library/selection/types';
import { getSlashParameters } from '../../library/utilities/slashParameterParser';
import { getAppMonitor } from '../../monitor';
import { matchUrlPattern } from '../../urlPattern';
import { RootState } from '..';
import { isLinkUser } from '../selectors/isLinkUser';
import { applicationStoreOrganizationAndCustomerGuid, startup } from '../slices/applicationSlice';
import { expandNavigationPanelRows } from '../slices/panelDataSlice';
import { SharedComponentSetState } from '../slices/sharedComponentSetSlice';
import { usageDataLoaded } from '../slices/usageDataSlice';
import { applyUserSettings } from '../slices/userSettingsSlice';
import {
  loadHangerConfigurations,
  loadJob,
  loadProject,
  loadSingleComponentDesign,
  loadSingleComponentDesignFromAnalysisSet,
  loadStationAndComponentDesigns,
} from './loadActions';
import {
  CHOOSE_PROJECT_DIALOG,
  showChooseProjectDialog,
  showSelectStationDialog,
  showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed,
  showWelcomeDialog,
} from './popupActions';
import { loadOrganizationSettingsIfNeeded } from './settingsActions';
import { loadSharedComponentSetContext } from './sharedComponentSetActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';
import { loadOptionalUserLayout } from './urlActions';

/**
 * Fetches and applies user settings.  If the settings have not arrived in five seconds, a warning message will be
 *   displayed, but the settings promise will be applied if the request eventually completes.
 */
const fetchUserSettings =
  (user: User): AsyncThunkActionVoid =>
  async (dispatch) => {
    let settingsConcluded = false;

    const warningMessage = enqueueWarning(
      'We are having trouble loading your custom settings, and they may not be applied correctly.',
    );
    const onSettingsProblem = () => dispatch(warningMessage);

    setTimeout(() => settingsConcluded || onSettingsProblem(), 5000);

    let settings: UserSettings;
    try {
      settings = await designService.userSettings.getUserSettings();
    } catch (error) {
      if ((error as AspNetStatusCodeResult).status === 404) {
        settings = await getFallbackUserSettings(user.organization, user.permissions);
      } else {
        onSettingsProblem();
        throw error;
      }
    } finally {
      settingsConcluded = true;
    }

    dispatch(applyUserSettings(settings));
  };

function switchFaviconForOrganization(state: RootState) {
  if (isG2User(state)) {
    const link: any = document.querySelector("link[rel*='shortcut icon']") || document.createElement('link');
    link.type = 'image/png';
    link.rel = 'shortcut icon';
    link.href = g2FaviconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  }
}

/**
 * New accounts are seeded with a single project, which is the one the welcome dialog is designed to be shown in.
 *   Returns `null` if it can't be identified; the welcome dialog reports that to the user itself.
 */
const identifySeededProjectForCurrentUser = (): AsyncThunkActionReturn<string | null> => async (dispatch) => {
  // the project will be loaded immediately after this, so we use the same spinner message to avoid flicker
  const spinnerId = dispatch(addSpinnerMessage('Loading project...'));

  try {
    const projectSummaries = await designService.projects.fetchSummaries();
    return projectSummaries.length !== 0 ? projectSummaries[0].guid : null;
  } catch (error) {
    return null;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const startApp =
  (appStartupTimerId?: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    switchFaviconForOrganization(state);
    const currentUser = getLoggedInUser(state);

    const userSettingsAppliedPromise =
      currentUser && !isLinkUser(state) ? dispatch(fetchUserSettings(currentUser)) : Promise.resolve();
    if (currentUser && !isLinkUser(state)) void dispatch(loadCurrentOrganizationMaterialSet());

    // Deliberately not awaited; whoever needs these can load them itself if they have not arrived
    void dispatch(loadOrganizationSettingsIfNeeded()).catch(() => console.error('Error loading organization settings'));

    getAppMonitor().appendMetadata({
      userEmail: currentUser?.email,
      monitoringSessionGuid: crypto.randomUUID(),
      organization: currentUser?.organization,
    });

    const slashParameters = getSlashParameters();
    const urlParameters = matchUrlPattern(window.location.pathname);
    const queryParameters = new URLSearchParams(window.location.search);

    let context;
    let shouldShowWelcomeDialog = false;

    if (queryParameters.has('station')) {
      const stationGuid = queryParameters.get('station')!;
      context = { type: 'station' as const, stationGuid };
    } else if (urlParameters.projectGuid && urlParameters.projectGuid !== '0') {
      if (urlParameters.projectGuid.length < 36) {
        const collectionIdentifier = urlParameters.projectGuid;
        if (getIsInSharedComponentSetContext(getState().sharedComponentSet)) {
          context = {
            type: 'shared-component-set' as const,
            sharedComponentSetLookupCode: collectionIdentifier,
          };
        } else {
          const jobIdInt = parseInt(collectionIdentifier, 10);
          if (!Number.isNaN(jobIdInt)) {
            context = { type: 'job' as const, jobId: jobIdInt };
          }
        }
      } else {
        context = { type: 'project' as const, projectGuid: urlParameters.projectGuid };
      }
    } else if ((hasFreeSubscription(state) || isWithinFreeTrialPeriod(currentUser)) && queryParameters.has('welcome')) {
      const seededProjectGuid = await dispatch(identifySeededProjectForCurrentUser());
      if (seededProjectGuid != null) {
        shouldShowWelcomeDialog = true;
        context = { type: 'project' as const, projectGuid: seededProjectGuid };
      }
    }

    if (context) {
      getAppMonitor().recordInteraction('Context Loaded', { context });
    }

    let selectedItem;

    let { selectedItemType } = urlParameters;
    const { selectedItemGuid, selectedLoadCaseGuid } = urlParameters;

    // DO NOT REMOVE - old /bearingEnvelope/ urls were created before beams existed, so they always point to walls.
    //   These URLs exist in the wild so must continue to respect them.
    const isLegacyBearingEnvelopeUrl = selectedItemType === LEGACY_BEARING_ENVELOPE_URL_TYPE;
    if (isLegacyBearingEnvelopeUrl) selectedItemType = 'wall';

    if (slashParameters.showLoads && selectedLoadCaseGuid != null) {
      selectedItem = { type: 'loadCase' as const, guid: selectedLoadCaseGuid };
    } else if (selectedItemGuid != null && selectedItemType != null && isSelectableItemType(selectedItemType)) {
      selectedItem = { type: selectedItemType, guid: selectedItemGuid };
    } else {
      selectedItem = null;
    }

    dispatch(
      startup({
        context,
        activeComponentDesignGuid: urlParameters.activeComponentDesignGuid ?? null,
        selectedItem,
        selectedLoadCaseGuid: urlParameters.selectedLoadCaseGuid ?? null,
        selectedAnalysisSetGuid: queryParameters.get('analysisSetGuid'),
      }),
    );

    if (currentUser && !isLinkUser(state))
      designService.usageData
        .getTrussDesignUsage()
        .then((usageData) => {
          dispatch(usageDataLoaded(usageData));

          const popup = getState().popup;
          const startupProjectSelectionIsOpen =
            popup.popupName === CHOOSE_PROJECT_DIALOG && popup.options.isStartup === true;

          if (!startupProjectSelectionIsOpen) {
            dispatch(showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed());
          }
        })
        .catch(() => void dispatch(enqueueError('Error loading usage data')));

    let contextIdentifier: string | undefined;
    switch (context?.type) {
      case 'job':
        contextIdentifier = context.jobId.toString();
        await dispatch(loadJob(context.jobId, urlParameters.activeComponentDesignGuid));
        break;

      case 'project':
        contextIdentifier = context.projectGuid;
        await dispatch(loadProject(context.projectGuid, urlParameters.activeComponentDesignGuid));
        break;

      case 'station':
        contextIdentifier = context.stationGuid;
        await dispatch(loadStationAndComponentDesigns(context.stationGuid, urlParameters.activeComponentDesignGuid));
        break;

      case 'shared-component-set':
        contextIdentifier = context.sharedComponentSetLookupCode;
        await dispatch(loadSharedComponentSetContext(context.sharedComponentSetLookupCode));
        break;

      case undefined:
        if (urlParameters.activeComponentDesignGuid) {
          if (queryParameters.has('analysisSetGuid')) {
            await dispatch(
              loadSingleComponentDesignFromAnalysisSet(
                urlParameters.activeComponentDesignGuid,
                queryParameters.get('analysisSetGuid')!,
              ),
            );
            break;
          } else {
            await dispatch(loadSingleComponentDesign(urlParameters.activeComponentDesignGuid));
            break;
          }
        }

        if (hasAssemblyPermission(state)) {
          dispatch(showSelectStationDialog());
          break;
        }

        dispatch(showChooseProjectDialog({ isStartup: true }));
        break;

      default:
        assertExhaustiveSwitch(context);
    }

    if (shouldShowWelcomeDialog) {
      dispatch(showWelcomeDialog());
    }

    if (contextIdentifier) {
      dispatch(
        expandNavigationPanelRows([
          contextIdentifier,
          PathUtilities.createForComponentDesignContainerComponentsCollection(contextIdentifier),
        ]),
      );
    }

    // If user settings haven't yet arrived, we need to make sure they're applied before applying query parameters.  But
    //   if we are unable to fetch user settings for some reason, we still want to apply the query parameters.
    userSettingsAppliedPromise.finally(() => dispatch(loadOptionalUserLayout(slashParameters)));

    await dispatch(loadHangerConfigurations());

    if (appStartupTimerId != null) getAppMonitor().concludeTimedOperation(appStartupTimerId);
  };

export const loginAndStartApp =
  (appStartupTimerId: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const params = new URLSearchParams(window.location.search);

    const organizationGuid = params.get('organizationGuid');
    const customerGuid = params.get('customerGuid');

    const transientJwtToken = params.get('jwt');

    dispatch(automaticLogInAttemptStarted());

    let user: User | null;
    try {
      user = await dispatch(logInWithStoredToken());

      if (!user) {
        if (organizationGuid && customerGuid) {
          user = await dispatch(logInWithGuids(organizationGuid, customerGuid));
          dispatch(
            applicationStoreOrganizationAndCustomerGuid({
              organizationGuid,
              customerGuid,
            }),
          );
        } else if (transientJwtToken) {
          user = await dispatch(logInWithTransientToken(transientJwtToken));
        }
      }
    } finally {
      dispatch(automaticLogInAttemptComplete());
    }

    if (user || getIsInSharedComponentSetContext(getState().sharedComponentSet)) dispatch(startApp(appStartupTimerId));
  };

/**
 * Provide a way to determine if we are in a SharedComponentSetContext before we are able to set that in the state. In
 * general, you should be using `state.application.openContext?.type === 'shared-component-set'`.
 */
export function getIsInSharedComponentSetContext(sharedComponentSetState: SharedComponentSetState) {
  if (sharedComponentSetState.errorLoadingSharedComponentSet) return false;
  const urlParameters = matchUrlPattern(window.location.pathname);
  const collectionIdentifier = urlParameters.projectGuid;
  // Project GUIDs are always 36 characters, and we don't expect job IDs to get this high
  return collectionIdentifier?.length === 10;
}
